import { MessagesSquare, Plus } from 'lucide-react'
import { useState } from 'react'
import { useOrganizerGroups } from '~/entities/organizer-group/model/use-organizer-groups'
import { useProfileScope } from '~/entities/profile/model/use-profiles'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'
import { GroupFormDialog } from './group-dialogs'
import { GroupThread } from './group-thread'

export function GroupsPage() {
  const { businessId, scope, isLoading: scopeLoading } = useProfileScope()
  const profileId = scope.kind === 'organization' ? undefined : scope.profileId
  const groups = useOrganizerGroups(businessId ?? '', profileId)
  const [picked, setPicked] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const list = groups.data ?? []
  const group = list.find((row) => row.id === picked) ?? list[0] ?? null

  const header = (
    <PageHeader
      title={m.groupsTitle()}
      description={m.groupsDescription()}
      action={
        profileId ? (
          <Button onClick={() => setCreating(true)}>
            <Plus />
            {m.groupsNew()}
          </Button>
        ) : undefined
      }
    />
  )

  if (!scopeLoading && !profileId) {
    return (
      <div className="flex w-full flex-col gap-6">
        {header}
        <Card className="p-6 text-muted-foreground text-sm">{m.groupsPickProfile()}</Card>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {header}
      {groups.isLoading || scopeLoading ? (
        <Skeleton className="h-96 w-full rounded-xl" />
      ) : list.length === 0 ? (
        <Card className="flex flex-col items-start gap-4 p-6 sm:p-8">
          <span className="flex size-10 items-center justify-center rounded-full bg-muted">
            <MessagesSquare aria-hidden className="size-5 text-muted-foreground" />
          </span>
          <div className="flex max-w-prose flex-col gap-1.5">
            <h2 className="font-semibold font-serif text-foreground text-xl tracking-tight">
              {m.groupsEmptyTitle()}
            </h2>
            <p className="text-muted-foreground text-sm leading-6">{m.groupsEmptyBody()}</p>
          </div>
          <Button onClick={() => setCreating(true)}>
            <Plus />
            {m.groupsNew()}
          </Button>
        </Card>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
          <Card className="overflow-hidden">
            <ul className="divide-y divide-border">
              {list.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setPicked(row.id)}
                    aria-current={row.id === group?.id}
                    className={cn(
                      'block w-full px-4 py-3 text-left outline-none transition-colors hover:bg-accent/60 focus-visible:bg-accent/60',
                      row.id === group?.id && 'bg-accent',
                    )}
                  >
                    <p className="truncate font-medium text-sm">{row.title}</p>
                    <p className="text-muted-foreground text-xs">
                      {m.groupsMemberCount({ count: row.memberCount })}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
          {group && businessId && profileId ? (
            <GroupThread
              key={group.id}
              businessId={businessId}
              profileId={profileId}
              showListed={scope.kind === 'organizer'}
              group={group}
            />
          ) : null}
        </div>
      )}
      {businessId && profileId ? (
        <GroupFormDialog
          open={creating}
          onOpenChange={setCreating}
          businessId={businessId}
          profileId={profileId}
          showListed={scope.kind === 'organizer'}
          group={null}
          onCreated={(created) => setPicked(created.id)}
        />
      ) : null}
    </div>
  )
}
